// "My Cases" beside a thing of the team (a message, an issue): how many of my
// Cases link to it, and the Case itself in a panel at the right of the screen,
// without leaving the screen. The parts that own those things put the chip.
//
// Promise:
// - The chip counts only my Cases that link to the thing (the refs'
//   backlinks; Cases are their owner's alone) and is absent when there are
//   none.
// - Pressing it opens the Case beside the screen (one Case at once; with
//   several, the person picks one). The Case is the same detail as Me's, and
//   can be written to. The window's URL does not change.
import { useQuery } from "@tanstack/react-query";
import { m } from "@trameria/cloud-i18n";
import {
  CasePanel,
  Icon,
  isImeComposing,
  Popover,
  useApi,
} from "@trameria/ui-core";
import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useWorkspaceHost } from "./context";
import { myCasesQuery } from "./queries";

export function MyCasesChip({ kind, id }: { kind: string; id: string }) {
  const cases = useQuery(myCasesQuery(useApi(), kind, id)).data ?? [];
  const anchor = useRef<HTMLButtonElement>(null);
  const [choosing, setChoosing] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  if (cases.length === 0 && open === null) return null;
  const count = cases.length;
  return (
    <>
      {count > 0 && (
        <button
          ref={anchor}
          type="button"
          aria-label={m.ws_my_cases_chip({ count })}
          aria-haspopup={count > 1 ? "menu" : undefined}
          aria-expanded={count > 1 ? choosing : undefined}
          onClick={() => {
            if (count === 1) setOpen(cases[0].id);
            else setChoosing((v) => !v);
          }}
          className="inline-flex h-[22px] flex-none cursor-pointer items-center gap-1 rounded-[11px] border border-border px-2 text-[11.5px] text-muted tabular-nums hover:border-field-strong hover:text-body"
        >
          <Icon kind="cases" size={13} />
          {count}
        </button>
      )}
      <Popover
        anchor={anchor}
        open={choosing}
        onClose={() => setChoosing(false)}
        label={m.ws_my_cases()}
      >
        <div role="menu" aria-label={m.ws_my_cases()}>
          {cases.map((c) => (
            <button
              key={c.id}
              type="button"
              role="menuitem"
              onClick={() => {
                setChoosing(false);
                setOpen(c.id);
              }}
              className="flex h-8 w-full min-w-0 cursor-pointer items-center gap-2 px-3 text-left text-[13px] text-body hover:bg-hover focus:bg-hover focus:outline-none"
            >
              <Icon kind="cases" size={14} className="text-muted" />
              <span className="min-w-0 truncate">{c.label}</span>
            </button>
          ))}
        </div>
      </Popover>
      {open !== null && (
        <CasePeekPanel caseId={open} onClose={() => setOpen(null)} />
      )}
    </>
  );
}

/** One Case at the right of the screen, over it, until closed. */
export function CasePeekPanel({
  caseId,
  onClose,
}: {
  caseId: string;
  onClose(): void;
}) {
  const { live } = useWorkspaceHost();
  return createPortal(
    <aside
      aria-label={m.ws_case_panel()}
      className="fixed top-2 right-2 bottom-2 z-30 flex w-[min(560px,calc(100vw-16px))] flex-col overflow-hidden rounded-[10px] border border-border bg-surface shadow-[var(--shadow-popover)]"
      onKeyDown={(e) => {
        // Escape in a field ends the writing there; in a menu or dialog it
        // closes that. Only an Escape that reaches the panel itself closes it.
        if (
          e.key === "Escape" &&
          !isImeComposing(e.nativeEvent) &&
          !(e.target as Element).closest(
            "input,textarea,[contenteditable=true],[role=dialog],[role=menu]",
          )
        )
          onClose();
      }}
    >
      <CasePanel caseId={caseId} transport={live} onClose={onClose} />
    </aside>,
    document.body,
  );
}
